import { RefObject, useCallback, useEffect, useRef } from "react"
import { DateTime } from "luxon"

/** epoch ms at the left and right edge of the scrolled timeline */
export type TimelineVisibleRange = Readonly<{
    start: number
    end: number
}>

type ChronoCell = Readonly<{
    left: number
    right: number
    start: number
    end: number
}>

// data-date is local time without an offset
const CHRONO_CELL_SELECTOR = ".fc-timeline-header-row-chrono th[data-date]"

function timeAt(cells: ReadonlyArray<ChronoCell>, x: number) {
    const cell = cells.find((candidate) => x < candidate.right) ?? cells[cells.length - 1]
    const width = cell.right - cell.left
    const ratio = width > 0 ? Math.min(1, Math.max(0, (x - cell.left) / width)) : 0

    return cell.start + ratio * (cell.end - cell.start)
}

function readVisibleRange(root: HTMLElement): TimelineVisibleRange | null {
    const elements = Array.from(root.querySelectorAll<HTMLElement>(CHRONO_CELL_SELECTOR))
    const scroller = elements[0]?.closest<HTMLElement>(".fc-scroller")
    if (!scroller) return null

    const starts = elements.map((el) => DateTime.fromISO(el.getAttribute("data-date") ?? "").toMillis())
    if (starts.some((start) => Number.isNaN(start))) return null

    const cells = elements.map<ChronoCell>((el, index) => {
        const { left, right } = el.getBoundingClientRect()
        const start = starts[index]
        const previousLength = index > 0 ? start - starts[index - 1] : 0

        return { left, right, start, end: starts[index + 1] ?? start + previousLength }
    })
    const viewLeft = scroller.getBoundingClientRect().left

    return { start: timeAt(cells, viewLeft), end: timeAt(cells, viewLeft + scroller.clientWidth) }
}

export function useTimelineVisibleRange(
    containerRef: RefObject<HTMLElement | null>,
    onChange: ((range: TimelineVisibleRange | null) => void) | undefined,
) {
    const frameRef = useRef<number | null>(null)

    const refresh = useCallback(() => {
        if (!onChange || frameRef.current !== null) return

        frameRef.current = window.requestAnimationFrame(() => {
            frameRef.current = null
            const root = containerRef.current
            if (root) onChange(readVisibleRange(root))
        })
    }, [containerRef, onChange])

    useEffect(() => {
        const root = containerRef.current
        if (!onChange || !root) return

        // scroll does not bubble; capture still sees every .fc-scroller, even re-created ones
        root.addEventListener("scroll", refresh, { capture: true, passive: true })
        const resizeObserver = new ResizeObserver(refresh)
        resizeObserver.observe(root)

        return () => {
            root.removeEventListener("scroll", refresh, { capture: true })
            resizeObserver.disconnect()
            if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
            frameRef.current = null
            onChange(null)
        }
    }, [containerRef, onChange, refresh])

    return refresh
}
